/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'
import type { FileAclResponse, AclUpdateRequest, AssembledUsersGroupsResponse } from '../../types/acl'

/**
 * API service for ACL (Access Control List) operations
 */
export class AclApiService {
  /**
   * Get ACL information for a specific file
   * Legacy endpoint: api/acls/{fsiId}
   */
  static async getFileACL(fsiId: number): Promise<FileAclResponse> {
    return baseApiService.fetch<FileAclResponse>(`/api/acls/${fsiId}`)
  }

  /**
   * Update ACL information for a specific file
   * Legacy endpoint: api/acls/{fsiId}
   */
  static async updateFileACL(fsiId: number, aclData: AclUpdateRequest): Promise<void> {
    return baseApiService.fetch<void>(`/api/acls/${fsiId}`, {
      method: 'PATCH',
      body: aclData
    })
  }

  /**
   * Get all available users and groups for ACL assignment
   */
  static async getAssembledUsersGroups(includeDisabled = false): Promise<AssembledUsersGroupsResponse> {
    return baseApiService.fetch<AssembledUsersGroupsResponse>('/api/users/assembled', {
      params: { includeDisabled }
    })
  }

  /**
   * Get ACL inheritance information for a file
   */
  static async getAclInheritance(fsiId: number): Promise<{ inherited: boolean; parentId?: number }> {
    return baseApiService.fetch<{ inherited: boolean; parentId?: number }>(`/api/files/${fsiId}/acl/inheritance`)
  }

  /**
   * Apply ACLs to multiple files
   */
  static async bulkUpdateAcls(fsiIds: number[], aclData: AclUpdateRequest): Promise<void> {
    return baseApiService.fetch<void>('/api/files/acl/bulk', {
      method: 'PUT',
      body: {
        fsiIds,
        ...aclData
      }
    })
  }
}

export default AclApiService
